import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  axApiClient,
  onboardingApiClient,
} from "@shared/api/clients/apiClientFactory";

export const axSystemsApi = {
  getFacilities: async (remoteUtilityId: number, is_all_data: number) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "facilities",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("is_all_data", is_all_data);
        return query;
      }
    );
    const response = await axApiClient.get(url);
    return response.data.results ?? [];
  },
  getChoices: async (type: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `choices/${type}`
    );
    const response = await axApiClient.get(url);
    return response.data.result ?? [];
  },

  getAllSystems: async (
    remoteUtilityId: number,
    is_pagination_required: number,
    filters?: Record<string, any>
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "systems",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("is_pagination_required", is_pagination_required);

        if (filters) {
          // Handle search
          if (filters.search) {
            query.push("search", filters.search);
          }

          // Handle status
          if (filters.status) {
            query.push("status", filters.status);
          }

          // Handle system_type
          if (filters.systemType) {
            query.push("system_type", filters.systemType);
          }

          // Handle criticality
          if (filters.criticality) {
            query.push("criticality", filters.criticality);
          }

          // Handle date range
          if (filters.startDate) {
            query.push("date_from", filters.startDate);
          }

          if (filters.endDate) {
            query.push("date_to", filters.endDate);
          }

          // Handle pagination
          if (filters.page) {
            query.push("page", filters.page);
          }

          if (filters.limit) {
            query.push("limit", filters.limit);
          }
        }

        return query;
      }
    );
    const response = await axApiClient.get(url);
    return response.data ?? [];
  },
  getDashboardData: async (remoteUtilityId: number) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "systems/dashboard",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        return query;
      }
    );
    const response = await axApiClient.get(url);
    return response.data ?? [];
  },
  createSystems: async (payload: any): Promise<any> => {
    try {
      const response = await axApiClient.post("systems/", payload);
      return response.data.result || response.data;
    } catch (error) {
      throw error;
    }
  },
  updateSystems: async (id: string | number, payload: any): Promise<any> => {
    try {
      const response = await axApiClient.patch(`systems/${id}/`, payload);
      return response.data.result || response.data;
    } catch (error) {
      throw error;
    }
  },

  updateDocuments: async (
    systemId: number,
    formData: FormData
  ): Promise<any> => {
    const url = ApiEndpoints.createUrl("ax", `systems/${systemId}/documents`);

    const response = await axApiClient.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },
  getAllUserInfo: async (remote_utility_id: number, role: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "auth",
      `user-info`,
      (query) => {
        query.push("remote_utility_id", remote_utility_id);
        query.push("role", role);
        return query;
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data.result ?? [];
  },
  getSystemDataById: async (id: number) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `systems/${id}`
    );
    const response = await axApiClient.get(url);
    return response.data.result ?? [];
  },
  getAllSystemTabDataById: async (
    id: number,
    tab_name: string,
    remoteUtilityId: number
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `systems/${id}/${tab_name}`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        if (tab_name === "performance") query.push("date_filter", "monthly");
        return query;
      }
    );
    const response = await axApiClient.get(url);
    return response.data.result ?? [];
  },
  getSystemAssetsTabDataById: async (
    id: number,
    tab_name: string,
    is_pagination_required?: number
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `systems/${id}/${tab_name}`,
      (query) => {
        if (is_pagination_required)
          query.push("is_pagination_required", is_pagination_required);
        return query;
      }
    );
    const response = await axApiClient.get(url);
    return response.data ?? [];
  },
  getAllUtilityServices: async (
    remote_utility_id: number,
    utility_service: string
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "auth",
      `utility/services`,
      (query) => {
        query.push("remote_utility_id", remote_utility_id);
        query.push("utility_service", utility_service);
        return query;
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data.result ?? [];
  },
  getAllcriticality: async (
    remote_utility_id: number,
    show_inactive: boolean
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      `config-map/criticality`,
      (query) => {
        query.push("remote_utility_id", remote_utility_id);
        query.push("show_inactive", show_inactive);
        return query;
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data.result ?? [];
  },
  getAllServiceOrder: async (
    remoteUtilityId: number,
    requestId: number,
    search: string,
    pagination: any
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "service-order",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("request_id", requestId);
        if (search) query.push("search_data", search);
        if (pagination.limit) query.push("limit", pagination.limit);
        if (pagination.currentPage) query.push("page", pagination.currentPage);

        return query;
      }
    );
    const response = await axApiClient.get(url);
    return response.data ?? [];
  },
  getSystemComplianceTabDataById: async (
    entityType: number,
    entityId: number,
    remoteUtilityId: number
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `compliance/parameter-monitoring`,
      (query) => {
        query.push("entity_type", entityType);
        query.push("entity_id", entityId);
        query.push("remote_utility_id", remoteUtilityId);
        return query;
      }
    );
    const response = await axApiClient.get(url);
    return response.data ?? [];
  },
  getSystemComplianceHistoryTabDataById: async (
    entityType: number,
    entityId: number,
    remoteUtilityId: number
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `compliance/history`,
      (query) => {
        query.push("entity_type", entityType);
        query.push("entity_id", entityId);
        query.push("remote_utility_id", remoteUtilityId);
        return query;
      }
    );
    const response = await axApiClient.get(url);
    return response.data ?? [];
  },
  getAllAssets: async (
    remoteUtilityId: number,
    page: number,
    limit: number,
    system: number,
    search: string
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `assets`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("page", page);
        query.push("limit", limit);
        query.push("system", system);
        if (search) query.push("search", search);
        return query;
      }
    );
    const response = await axApiClient.get(url);
    return response.data ?? [];
  },
  getSystemType: async (
    remoteUtilityId: number,
    configLevel?: string,
    showInactive?: boolean
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/system_json",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("config_level", configLevel);
        query.push("show_inactive", showInactive);
        return query;
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data.result ?? [];
  },

  getAssets: async (remoteUtilityId: string, filters: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "assets",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("is_pagination_required", 1);

        if (filters.systemId) {
          query.push("system_id", filters.systemId);
        }

        query.push("page", filters.page || 1);
        query.push("limit", filters.limit || 25);

        if (filters.searchData) {
          query.push("search", filters.searchData);
        }

        if (filters.assetType) {
          query.push("asset_type", filters.assetType[0]);
        }
      }
    );

    const response = await axApiClient.get(url);
    return response.data;
  },
  getDownloadBill: async (dataTypeName: string): Promise<Blob> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "csv-document-generator",
      (query) => {
        query.push("data_type_name", dataTypeName);
      }
    );

    const response = await axApiClient.get(url, {
      responseType: "blob",
    });

    return response.data;
  },

  uploadEnergyData: async (formData: FormData): Promise<any> => {
    const url = ApiEndpoints.createUrl("ax", "upload-readings");

    const response = await axApiClient.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },
  getAllEnergyConsumptionHistory: async (remoteUtilityId: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `energy-readings`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        return query;
      }
    );
    const response = await axApiClient.get(url);
    return response.data ?? [];
  },

  deleteSystem: async (
    systemId: string,
    remoteUtilityId: string
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `systems/${systemId}`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      }
    );

    const response = await axApiClient.delete(url);
    return response.data;
  },

  getSystemParameters: async (
    remoteUtilityId: string,
    systemType: string
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/system_json",
      (qs) => {
        qs.push("remote_utility_id", remoteUtilityId);
        qs.push("config_level", "system_parameter");
        qs.push("show_inactive", "true ");
        qs.push("code_list", systemType);
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data.result;
  },
};
